import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS } from "@/i18n";

export default function LanguageSwitcher({ variant = "desktop" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current =
    SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-toggle"
        className={`inline-flex items-center gap-2 text-[#1a365d] hover:text-[#d4af37] transition-colors ${
          variant === "desktop" ? "h-10 px-2.5" : "h-9 px-2"
        }`}
        aria-label={t("common.changeLanguage")}
      >
        <Globe size={16} strokeWidth={1.6} />
        <span className="text-[11px] tracking-[0.18em] uppercase font-body">
          {current.code.toUpperCase()}
        </span>
        <ChevronDown
          size={12}
          strokeWidth={2}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          data-testid="language-switcher-menu"
          className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#e2e8f0] shadow-[0_20px_50px_-20px_rgba(15,36,64,0.25)] z-50"
        >
          <div className="p-2">
            {SUPPORTED_LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => handleSelect(l.code)}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-[#fafaf7] transition-colors ${
                  current.code === l.code ? "bg-[#fafaf7]" : ""
                }`}
              >
                <span className="flex items-center gap-3">
                  <img
                    src={`https://flagcdn.com/24x18/${l.country}.png`}
                    srcSet={`https://flagcdn.com/48x36/${l.country}.png 2x`}
                    width="24"
                    height="18"
                    alt=""
                    className="border border-[#e2e8f0]"
                    loading="lazy"
                  />
                  <span className="font-body text-[13px] text-[#1a365d]">
                    {l.label}
                  </span>
                </span>
                {current.code === l.code && (
                  <Check size={14} className="text-[#d4af37]" strokeWidth={2} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
